
import window from 'bits/global'
import { Component, Vue } from 'bits/vue'

const FOCUSABLE_ELEMENTS_QUERY = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  'select:not([disabled]):not([aria-hidden])',
  'textarea:not([disabled]):not([aria-hidden])',
  'button:not([disabled]):not([aria-hidden])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex^="-"])',
].join(', ')

function isElementContained(rootElement: HTMLElement, activeElement: Element | null): boolean {
  if (!activeElement) return false

  const activeElementRelativePosition = rootElement.compareDocumentPosition(activeElement)
  if (!activeElementRelativePosition) return false

  const isContained = (activeElementRelativePosition & Node.DOCUMENT_POSITION_CONTAINED_BY) > 0
  return isContained
}

@Component({})
export default class FocusTrap extends Vue {
  lastFocusedElement: Element | null = null

  afterFocusShiftsFlushed(callback): void {
    // Only check and ensure focus is contained after a 1ms `setTimeout`.
    // This is to allow `focus()` calls on child elements to take place first.
    // For that to happen, these calls should happen immediately or by a 0ms `setTimeout` at the latest.
    setTimeout(() => this.$nextTick(callback), 1)
  }

  getFocusTarget(event: KeyboardEvent): HTMLElement | null {
    const isTabPressed = event.key === 'Tab'
    const isShiftPressed = event.shiftKey

    // Prevent DOM searching if the user is not navigating with the keyboard.
    if (!isTabPressed) return null

    const rootElement = this.$el as HTMLElement
    const activeElement = document.activeElement
    const isAlreadyTrapped = isElementContained(rootElement, activeElement)
    const focusableElements = rootElement.querySelectorAll(FOCUSABLE_ELEMENTS_QUERY)

    const firstFocusableElement = focusableElements[0] as HTMLElement
    const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement

    if (isShiftPressed) {
      // Shift + Tab
      if (activeElement === firstFocusableElement || !isAlreadyTrapped) {
        event.preventDefault()
        return lastFocusableElement || rootElement
      }
    } else {
      // Tab
      if (activeElement === lastFocusableElement || !isAlreadyTrapped) {
        event.preventDefault()
        return firstFocusableElement || rootElement
      }
    }

    return null
  }

  ensureFocusTrapped(event: KeyboardEvent): void {
    const isLatestDialog = allFocusTraps[0] === this
    if (!isLatestDialog) return

    const focusTarget = this.getFocusTarget(event)
    if (focusTarget) focusTarget.focus()
  }

  created(): void {
    // Save last focused element.
    this.lastFocusedElement = window.document.activeElement
  }

  mounted(): void {
    allFocusTraps.unshift(this)

    // Focus dialog first.
    this.afterFocusShiftsFlushed(() => {
      const rootElement = this.$el as HTMLElement
      const previousActiveElement = document.activeElement as HTMLElement | null

      if (!isElementContained(rootElement, previousActiveElement)) {
        rootElement.focus()
      }
    })

    window.addEventListener('keydown', this.ensureFocusTrapped)
  }

  beforeDestroy(): void {
    const currentFocusTrapIndex = allFocusTraps.indexOf(this)

    // Remove current FocusTrap instance from array.
    allFocusTraps.splice(currentFocusTrapIndex, 1)

    /**
     *  Restore focus to latest focused element.
     */
    if (this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus()
    }

    window.removeEventListener('keydown', this.ensureFocusTrapped)
  }
}

const allFocusTraps: FocusTrap[] = []
